import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  LOCALE_ID,
  signal,
} from '@angular/core';
import { DateService } from '@softline/core';
import {
  BadgeComponent,
  ButtonComponent,
  CardComponent,
  EmptyStateComponent,
  FormatPipe,
  IconComponent,
  LoadingSkeletonComponent,
  NumberInputComponent,
} from '@softline/ui-core';
import { MileageHistory } from '../../models/milage-history';
import { MileageHistoryService } from '../../services/mileage-history.service';
import { formatDateForRequest } from '../../utils/format-date-for-request.function';

@Component({
  selector: 'soft-vehicle-mileage-list',
  imports: [
    NgClass,
    FormatPipe,
    IconComponent,
    ButtonComponent,
    NumberInputComponent,
    CardComponent,
    BadgeComponent,
    EmptyStateComponent,
    LoadingSkeletonComponent,
  ],
  templateUrl: './vehicle-mileage-list.component.html',
  styleUrl: './vehicle-mileage-list.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VehicleMileageListComponent {
  readonly vehicleId = input.required<string>();
  readonly createDisabled = input<boolean>(false);

  readonly context = input<'vehicle' | 'order'>('vehicle');

  readonly initialMileage = input<number | null>(null);
  readonly initialOperatingHours = input<number | null>(null);

  readonly #mileageService = inject(MileageHistoryService);
  readonly #dateService = inject(DateService);
  readonly #locale = inject(LOCALE_ID);

  readonly submitLabel = computed(() =>
    this.context() === 'order' ? 'Auf Auftrag erfassen' : 'Manuell erfassen'
  );

  readonly mileage = computed(() => this.#mileageService.mileageHistory());
  readonly loading = computed(() => this.#mileageService.mileageHistoryLoading());
  readonly error = computed(() => this.#mileageService.mileageHistoryError());

  readonly latest = computed<MileageHistory | undefined>(() => this.mileage()[0]);
  readonly currentMileage = computed(() => this.latest()?.mileage ?? null);
  readonly currentOperatingHours = computed(() => this.latest()?.operatingHours ?? null);

  readonly newMileage = signal<number | null>(null);
  readonly newOperatingHours = signal<number | null>(null);

  readonly canSubmitManual = computed(
    () =>
      !this.createDisabled() &&
      (this.newMileage() != null || this.newOperatingHours() != null)
  );

  #prefilled = false;

  constructor() {
    effect(() => {
      const id = this.vehicleId();
      if (!id) return;
      this.#mileageService.loadMileageHistory(Number(id));
    });

    effect(() => {
      const mileage = this.initialMileage();
      const hours = this.initialOperatingHours();
      if (this.#prefilled || (mileage == null && hours == null)) return;
      this.#prefilled = true;
      if (mileage != null) this.newMileage.set(mileage);
      if (hours != null) this.newOperatingHours.set(hours);
    });
  }

  isManual(entry: MileageHistory): boolean {
    return entry.documentNumber == null;
  }


  hasNoReading(entry: MileageHistory): boolean {
    return entry.id === -1 || (entry.mileage == null && entry.operatingHours == null);
  }

  num(value: number | null | undefined): number {
    return value ?? 0;
  }

  stripClass(entry: MileageHistory): string {
    if (this.isDone(entry.orderStatus)) {
      return 'bg-(--ds-surface-status-success-muted)';
    }
    return this.isManual(entry)
      ? 'bg-(--ds-surface-status-warning-muted)'
      : 'bg-(--ds-surface-brand-muted)';
  }

  isDone(status: string): boolean {
    return status?.toUpperCase() === 'E';
  }

  statusLabel(status: string): string {
    switch (status?.toUpperCase()) {
      case 'O':
        return 'Offen';
      case 'E':
        return 'Erledigt';
      default:
        return status ?? '';
    }
  }

  statusVariant(status: string): 'success' | 'warning' | 'secondary' {
    switch (status?.toUpperCase()) {
      case 'E':
        return 'success';
      case 'O':
        return 'warning';
      default:
        return 'secondary';
    }
  }

  async submitManual(): Promise<void> {
    if (!this.canSubmitManual()) return;
    await this.#mileageService.addMileage(this.vehicleId(), {
      date: formatDateForRequest(this.#dateService.now(), this.#locale),
      mileage: this.newMileage() ?? 0,
      operatingHours: this.newOperatingHours() ?? 0,
    });
    this.newMileage.set(null);
    this.newOperatingHours.set(null);
  }
}
